import React, { useEffect, useState, useRef, createElement } from 'react';
import { motion, AnimatePresence, Reorder } from 'framer-motion';
import { Plus, Edit2, Trash2, X, Save, Upload, Download, FileUp, CheckCircle, GripVertical, LogOut } from 'lucide-react';
import { Magnetic } from '../components/ui/Magnetic';
import { UploadProgress } from '../components/UploadProgress';
import { supabase, uploadImage, getImageUrl } from '../lib/supabase';
import type { Project, ProjectsPageItem } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';
interface AdminPageProps {
  onUpdateProjects: () => Promise<void>;
  onUpdateProjectsPageData: () => Promise<void>;
  onBack: () => void;
}
type Section = 'work' | 'projects';
export function AdminPage({
  onUpdateProjects,
  onUpdateProjectsPageData,
  onBack
}: AdminPageProps) {
  const [activeSection, setActiveSection] = useState<Section>('work');
  const [isEditing, setIsEditing] = useState(false);
  const [editingProject, setEditingProject] = useState<Project | null>(null);
  const [editingProjectsItem, setEditingProjectsItem] = useState<ProjectsPageItem | null>(null);
  const [projects, setProjects] = useState<Project[]>([]);
  const [projectsPageData, setProjectsPageData] = useState<ProjectsPageItem[]>([]);
  const [isLoadingData, setIsLoadingData] = useState(true);
  const [notification, setNotification] = useState<{
    show: boolean;
    message: string;
    type: 'success' | 'error';
  }>({
    show: false,
    message: '',
    type: 'success'
  });
  const [formData, setFormData] = useState<Partial<Project>>({
    title: '',
    category: [],
    year: '2025',
    image_path: '',
    thumb_path: '',
    media_type: 'image',
    description: '',
    position: 0
  });
  const [projectsFormData, setProjectsFormData] = useState<Partial<ProjectsPageItem>>({
    title: '',
    description: '',
    image_path: '',
    link: '',
    category: 'Web App',
    button_text: 'View Project',
    button_disabled: false,
    position: 0
  });
  const [tempImageFile, setTempImageFile] = useState<File | null>(null);
  const [tempThumbnailFile, setTempThumbnailFile] = useState<File | null>(null);
  const [tempImagePreview, setTempImagePreview] = useState<string>('');
  const [tempThumbnailPreview, setTempThumbnailPreview] = useState<string>('');
  const [isDragging, setIsDragging] = useState(false);
  const [isThumbnailDragging, setIsThumbnailDragging] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<{
    show: boolean;
    progress: number;
    fileName: string;
    fileSize: number;
    uploadedSize: number;
  }>({
    show: false,
    progress: 0,
    fileName: '',
    fileSize: 0,
    uploadedSize: 0
  });
  const [isUploading, setIsUploading] = useState(false);
  const uploadAbortController = useRef<AbortController | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const thumbnailInputRef = useRef<HTMLInputElement>(null);
  const importInputRef = useRef<HTMLInputElement>(null);
  const projectsImportInputRef = useRef<HTMLInputElement>(null);
  const {
    signOut,
    user
  } = useAuth();
  const categories = ['Design', 'Posters', 'Video', 'UX/UI', 'Apps'];
  const projectsCategories = ['NFT Collection', 'Web App'];
  // Load data immediately on mount
  useEffect(() => {
    loadData();
  }, [activeSection]);
  const loadData = async () => {
    setIsLoadingData(true);
    try {
      if (activeSection === 'work') {
        const {
          data,
          error
        } = await supabase.from('projects').select('*').order('position', {
          ascending: true
        });
        if (error) throw error;
        if (data) {
          console.log('Admin loaded projects:', data);
          setProjects(data as Project[]);
        }
      } else {
        const {
          data,
          error
        } = await supabase.from('projects_page').select('*').order('position', {
          ascending: true
        });
        if (error) throw error;
        if (data) {
          console.log('Admin loaded projects page:', data);
          setProjectsPageData(data as ProjectsPageItem[]);
        }
      }
    } catch (error) {
      console.error('Error loading data:', error);
      showNotification('Error loading data', 'error');
    } finally {
      setIsLoadingData(false);
    }
  };
  const handleSectionChange = async (section: Section) => {
    setActiveSection(section);
    setIsEditing(false);
  };
  const showNotification = (message: string, type: 'success' | 'error' = 'success') => {
    setNotification({
      show: true,
      message,
      type
    });
    setTimeout(() => {
      setNotification({
        show: false,
        message: '',
        type: 'success'
      });
    }, 3000);
  };
  const handleSignOut = async () => {
    await signOut();
    onBack();
  };
  const isVideoCategory = () => {
    const categories = Array.isArray(formData.category) ? formData.category : formData.category ? [formData.category] : [];
    return categories.includes('Video');
  };
  const handleEdit = (project: Project) => {
    setEditingProject(project);
    setFormData({
      ...project,
      category: project.category
    });
    setTempImagePreview(getImageUrl(project.image_path) || '');
    setTempThumbnailPreview(getImageUrl(project.thumb_path) || '');
    setIsEditing(true);
  };
  const handleEditProjectsItem = (item: ProjectsPageItem) => {
    setEditingProjectsItem(item);
    setProjectsFormData(item);
    setTempImagePreview(getImageUrl(item.image_path) || '');
    setIsEditing(true);
  };
  const handleDelete = async (id: string) => {
    if (confirm('Are you sure you want to delete this project?')) {
      const {
        error
      } = await supabase.from('projects').delete().eq('id', id);
      if (error) {
        showNotification('Error deleting project', 'error');
        return;
      }
      showNotification('Project deleted successfully', 'success');
      await loadData();
      await onUpdateProjects();
    }
  };
  const handleDeleteProjectsItem = async (id: string) => {
    if (confirm('Are you sure you want to delete this project?')) {
      const {
        error
      } = await supabase.from('projects_page').delete().eq('id', id);
      if (error) {
        showNotification('Error deleting project', 'error');
        return;
      }
      showNotification('Project deleted successfully', 'success');
      await loadData();
      await onUpdateProjectsPageData();
    }
  };
  const handleCategoryToggle = (cat: string) => {
    const currentCategories = Array.isArray(formData.category) ? formData.category : formData.category ? [formData.category] : [];
    const newCategories = currentCategories.includes(cat) ? currentCategories.filter(c => c !== cat) : [...currentCategories, cat];
    setFormData({
      ...formData,
      category: newCategories
    });
  };
  const handleFileChange = (file: File) => {
    const isImage = file.type.startsWith('image/');
    const isVideo = file.type.startsWith('video/');
    if (!isImage && !isVideo) {
      showNotification('Please upload a valid image or video file', 'error');
      return;
    }
    setTempImageFile(file);
    const reader = new FileReader();
    reader.onloadend = () => {
      setTempImagePreview(reader.result as string);
      if (activeSection === 'work') {
        setFormData({
          ...formData,
          media_type: isVideo ? 'video' : 'image'
        });
      }
    };
    reader.readAsDataURL(file);
  };
  const handleThumbnailChange = (file: File) => {
    if (!file.type.startsWith('image/')) {
      showNotification('Please upload a valid image file for thumbnail', 'error');
      return;
    }
    setTempThumbnailFile(file);
    const reader = new FileReader();
    reader.onloadend = () => {
      setTempThumbnailPreview(reader.result as string);
    };
    reader.readAsDataURL(file);
  };
  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files[0];
    if (file) handleFileChange(file);
  };
  const handleThumbnailDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsThumbnailDragging(false);
    const file = e.dataTransfer.files[0];
    if (file) handleThumbnailChange(file);
  };
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsUploading(true);
    try {
      if (activeSection === 'work') {
        const categoryArray = Array.isArray(formData.category) ? formData.category : formData.category ? [formData.category] : [];
        if (!formData.title || categoryArray.length === 0) {
          showNotification('Title and at least one Category are required', 'error');
          setIsUploading(false);
          return;
        }
        if (!tempImageFile && !editingProject) {
          showNotification('Image is required', 'error');
          setIsUploading(false);
          return;
        }
        if (categoryArray.includes('Video') && !tempThumbnailFile && !editingProject?.thumb_path) {
          showNotification('Thumbnail is required for Video category', 'error');
          setIsUploading(false);
          return;
        }
        let imagePath = editingProject?.image_path || '';
        let thumbPath = editingProject?.thumb_path || '';
        if (tempImageFile) {
          const projectId = editingProject?.id || crypto.randomUUID();
          const ext = tempImageFile.name.split('.').pop();
          const path = `projects/${projectId}/image.${ext}`;
          const {
            url,
            error
          } = await uploadImage(tempImageFile, path);
          if (error || !url) {
            const errorMessage = typeof error === 'string' ? error : error.message || 'Unknown error';
            showNotification(`Error uploading image: ${errorMessage}`, 'error');
            setIsUploading(false);
            return;
          }
          imagePath = path;
        }
        if (tempThumbnailFile) {
          const projectId = editingProject?.id || crypto.randomUUID();
          const ext = tempThumbnailFile.name.split('.').pop();
          const path = `projects/${projectId}/thumb.${ext}`;
          const {
            url,
            error
          } = await uploadImage(tempThumbnailFile, path);
          if (error || !url) {
            const errorMessage = typeof error === 'string' ? error : error.message || 'Unknown error';
            showNotification(`Error uploading thumbnail: ${errorMessage}`, 'error');
            setIsUploading(false);
            return;
          }
          thumbPath = path;
        }
        const projectData = {
          title: formData.title,
          category: categoryArray,
          year: formData.year || '2025',
          image_path: imagePath,
          thumb_path: thumbPath || null,
          media_type: formData.media_type || 'image',
          description: formData.description || null,
          position: editingProject ? editingProject.position : projects.length
        };
        if (editingProject) {
          const {
            error
          } = await supabase.from('projects').update(projectData).eq('id', editingProject.id);
          if (error) {
            showNotification(`Error updating project: ${error.message}`, 'error');
            setIsUploading(false);
            return;
          }
          showNotification('Project updated successfully!', 'success');
        } else {
          const {
            error
          } = await supabase.from('projects').insert([projectData]);
          if (error) {
            showNotification(`Error creating project: ${error.message}`, 'error');
            setIsUploading(false);
            return;
          }
          showNotification('Project created successfully!', 'success');
        }
        await loadData();
        await onUpdateProjects();
      } else {
        if (!projectsFormData.title || !projectsFormData.description || !projectsFormData.category) {
          showNotification('Title, Description, and Category are required', 'error');
          setIsUploading(false);
          return;
        }
        if (!tempImageFile && !editingProjectsItem) {
          showNotification('Image is required', 'error');
          setIsUploading(false);
          return;
        }
        let imagePath = editingProjectsItem?.image_path || '';
        if (tempImageFile) {
          const projectId = editingProjectsItem?.id || crypto.randomUUID();
          const ext = tempImageFile.name.split('.').pop();
          const path = `projects-page/${projectId}/image.${ext}`;
          const {
            url,
            error
          } = await uploadImage(tempImageFile, path);
          if (error || !url) {
            const errorMessage = typeof error === 'string' ? error : error.message || 'Unknown error';
            showNotification(`Error uploading image: ${errorMessage}`, 'error');
            setIsUploading(false);
            return;
          }
          imagePath = path;
        }
        const projectData = {
          title: projectsFormData.title,
          description: projectsFormData.description,
          image_path: imagePath,
          link: projectsFormData.link || null,
          category: projectsFormData.category,
          button_text: projectsFormData.button_text || 'View Project',
          button_disabled: projectsFormData.button_disabled || false,
          position: editingProjectsItem ? editingProjectsItem.position : projectsPageData.length
        };
        if (editingProjectsItem) {
          const {
            error
          } = await supabase.from('projects_page').update(projectData).eq('id', editingProjectsItem.id);
          if (error) {
            showNotification(`Error updating project: ${error.message}`, 'error');
            setIsUploading(false);
            return;
          }
          showNotification('Project updated successfully!', 'success');
        } else {
          const {
            error
          } = await supabase.from('projects_page').insert([projectData]);
          if (error) {
            showNotification(`Error creating project: ${error.message}`, 'error');
            setIsUploading(false);
            return;
          }
          showNotification('Project created successfully!', 'success');
        }
        await loadData();
        await onUpdateProjectsPageData();
      }
      setIsEditing(false);
      setEditingProject(null);
      setEditingProjectsItem(null);
      setTempImageFile(null);
      setTempThumbnailFile(null);
      setTempImagePreview('');
      setTempThumbnailPreview('');
      setFormData({
        title: '',
        category: [],
        year: '2025',
        image_path: '',
        thumb_path: '',
        media_type: 'image',
        description: '',
        position: 0
      });
      setProjectsFormData({
        title: '',
        description: '',
        image_path: '',
        link: '',
        category: 'Web App',
        button_text: 'View Project',
        button_disabled: false,
        position: 0
      });
    } catch (error: any) {
      console.error('Submit error:', error);
      showNotification(`Error saving project: ${error.message || 'Unknown error'}`, 'error');
    } finally {
      setIsUploading(false);
    }
  };
  const handleCancel = () => {
    setIsEditing(false);
    setEditingProject(null);
    setEditingProjectsItem(null);
    setTempImageFile(null);
    setTempThumbnailFile(null);
    setTempImagePreview('');
    setTempThumbnailPreview('');
    setFormData({
      title: '',
      category: [],
      year: '2025',
      image_path: '',
      thumb_path: '',
      media_type: 'image',
      description: '',
      position: 0
    });
    setProjectsFormData({
      title: '',
      description: '',
      image_path: '',
      link: '',
      category: 'Web App',
      button_text: 'View Project',
      button_disabled: false,
      position: 0
    });
  };
  const handleNewProject = () => {
    setEditingProject(null);
    setEditingProjectsItem(null);
    setTempImageFile(null);
    setTempThumbnailFile(null);
    setTempImagePreview('');
    setTempThumbnailPreview('');
    setFormData({
      title: '',
      category: [],
      year: '2025',
      image_path: '',
      thumb_path: '',
      media_type: 'image',
      description: '',
      position: 0
    });
    setProjectsFormData({
      title: '',
      description: '',
      image_path: '',
      link: '',
      category: 'Web App',
      button_text: 'View Project',
      button_disabled: false,
      position: 0
    });
    setIsEditing(true);
  };
  const handleExport = () => {
    const data = activeSection === 'work' ? projects : projectsPageData;
    const dataStr = JSON.stringify(data, null, 2);
    const dataBlob = new Blob([dataStr], {
      type: 'application/json'
    });
    const url = URL.createObjectURL(dataBlob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${activeSection}-export-${new Date().toISOString().split('T')[0]}.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    showNotification(`${activeSection === 'work' ? 'Work' : 'Projects'} exported successfully!`, 'success');
  };
  const handleImport = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.type !== 'application/json') {
      showNotification('Please upload a valid JSON file', 'error');
      return;
    }
    const reader = new FileReader();
    reader.onload = async event => {
      try {
        const importedData = JSON.parse(event.target?.result as string);
        if (!Array.isArray(importedData)) {
          showNotification('Invalid JSON format: Expected an array', 'error');
          return;
        }
        const currentData = activeSection === 'work' ? projects : projectsPageData;
        if (confirm(`This will replace all ${currentData.length} existing items with ${importedData.length} imported items. Continue?`)) {
          if (activeSection === 'work') {
            await supabase.from('projects').delete().neq('id', '');
            const {
              error
            } = await supabase.from('projects').insert(importedData);
            if (error) throw error;
            await loadData();
            await onUpdateProjects();
          } else {
            await supabase.from('projects_page').delete().neq('id', '');
            const {
              error
            } = await supabase.from('projects_page').insert(importedData);
            if (error) throw error;
            await loadData();
            await onUpdateProjectsPageData();
          }
          showNotification(`${activeSection === 'work' ? 'Work' : 'Projects'} imported successfully!`, 'success');
        }
      } catch (error) {
        showNotification('Error importing data', 'error');
        console.error('Import error:', error);
      }
    };
    reader.readAsText(file);
    e.target.value = '';
  };
  const handleReorder = async (newOrder: any[]) => {
    const updatedData = newOrder.map((item, index) => ({
      ...item,
      position: index
    }));
    if (activeSection === 'work') {
      for (const item of updatedData) {
        await supabase.from('projects').update({
          position: item.position
        }).eq('id', item.id);
      }
      await loadData();
      await onUpdateProjects();
    } else {
      for (const item of updatedData) {
        await supabase.from('projects_page').update({
          position: item.position
        }).eq('id', item.id);
      }
      await loadData();
      await onUpdateProjectsPageData();
    }
  };
  const handleCancelUpload = () => {
    if (uploadAbortController.current) {
      uploadAbortController.current.abort();
    }
    setUploadProgress({
      show: false,
      progress: 0,
      fileName: '',
      fileSize: 0,
      uploadedSize: 0
    });
    setIsUploading(false);
    showNotification('Upload cancelled', 'error');
  };
  const getCategoryDisplay = (category: string | string[]) => {
    if (Array.isArray(category)) {
      return category.join(', ');
    }
    return category;
  };
  const getDisplayImage = (project: Project) => {
    const categories = Array.isArray(project.category) ? project.category : [project.category];
    if (categories.includes('Video') && project.thumb_path) {
      return getImageUrl(project.thumb_path);
    }
    return getImageUrl(project.image_path);
  };
  const currentData = activeSection === 'work' ? projects : projectsPageData;
  return <div className="bg-black text-white min-h-screen selection:bg-white selection:text-black" data-id="element-234">
      <AnimatePresence data-id="element-235">
        {uploadProgress.show && <UploadProgress progress={uploadProgress.progress} fileName={uploadProgress.fileName} fileSize={uploadProgress.fileSize} uploadedSize={uploadProgress.uploadedSize} onCancel={handleCancelUpload} data-id="element-236" />}
      </AnimatePresence>

      <AnimatePresence data-id="element-237">
        {notification.show && <motion.div initial={{
        opacity: 0,
        y: -50
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={{
        opacity: 0,
        y: -50
      }} className="fixed top-24 right-8 z-50 p-4 rounded-sm border flex items-center gap-3 min-w-[300px]" style={{
        backgroundColor: notification.type === 'success' ? 'rgba(34, 197, 94, 0.1)' : 'rgba(239, 68, 68, 0.1)',
        borderColor: notification.type === 'success' ? 'rgba(34, 197, 94, 0.3)' : 'rgba(239, 68, 68, 0.3)'
      }} data-id="element-238">
            <CheckCircle className="w-5 h-5 flex-shrink-0" style={{
          color: notification.type === 'success' ? '#22c55e' : '#ef4444'
        }} data-id="element-239" />
            <p style={{
          color: notification.type === 'success' ? '#22c55e' : '#ef4444'
        }} data-id="element-240">
              {notification.message}
            </p>
          </motion.div>}
      </AnimatePresence>

      <nav className="fixed top-0 left-0 w-full p-6 md:p-12 flex justify-between items-center z-40 bg-black/80 backdrop-blur-sm border-b border-white/10" data-id="element-241">
        <Magnetic data-id="element-242">
          <button onClick={onBack} className="text-2xl font-black tracking-tighter transition-opacity duration-300 hover:opacity-70" data-id="element-243">
            Jav.
          </button>
        </Magnetic>
        <div className="flex items-center gap-4" data-id="element-244">
          <span className="text-sm uppercase tracking-widest font-light" style={{
          color: '#999'
        }} data-id="element-245">
            {user?.email}
          </span>
          <button onClick={handleSignOut} className="px-4 py-2 border border-white/20 text-white font-bold rounded-full flex items-center gap-2 transition-opacity duration-300 hover:opacity-70" data-id="element-246">
            <LogOut className="w-4 h-4" data-id="element-247" />
            <span className="hidden md:inline" data-id="element-248">Sign Out</span>
          </button>
        </div>
      </nav>

      <main className="pt-48 pb-24 px-4 md:px-12" data-id="element-249">
        <div className="max-w-7xl mx-auto" data-id="element-250">
          <div className="flex gap-4 mb-8 border-b border-white/20 pb-4" data-id="element-251">
            <button onClick={() => handleSectionChange('work')} className={`px-6 py-3 rounded-full font-bold transition-all ${activeSection === 'work' ? 'bg-white text-black' : 'bg-transparent text-white border border-white/20 hover:border-white/40'}`} data-id="element-252">
              Work (Portfolio)
            </button>
            <button onClick={() => handleSectionChange('projects')} className={`px-6 py-3 rounded-full font-bold transition-all ${activeSection === 'projects' ? 'bg-white text-black' : 'bg-transparent text-white border border-white/20 hover:border-white/40'}`} data-id="element-253">
              Projects (Projects Page)
            </button>
          </div>

          <div className="flex items-end justify-between mb-16 border-b border-white/20 pb-8" data-id="element-254">
            <h1 className="text-4xl md:text-6xl font-bold tracking-tight" data-id="element-255">
              Manage {activeSection === 'work' ? 'Work' : 'Projects'}
            </h1>
            <div className="flex gap-3" data-id="element-256">
              <button onClick={() => activeSection === 'work' ? importInputRef.current?.click() : projectsImportInputRef.current?.click()} className="px-4 py-3 border border-white/20 text-white font-bold rounded-full flex items-center gap-2 transition-opacity duration-300 hover:opacity-70" data-id="element-257">
                <FileUp className="w-4 h-4" data-id="element-258" />
                <span className="hidden md:inline" data-id="element-259">Import</span>
              </button>
              <input ref={importInputRef} type="file" accept="application/json" onChange={handleImport} className="hidden" data-id="element-260" />
              <input ref={projectsImportInputRef} type="file" accept="application/json" onChange={handleImport} className="hidden" data-id="element-261" />
              <button onClick={handleExport} className="px-4 py-3 border border-white/20 text-white font-bold rounded-full flex items-center gap-2 transition-opacity duration-300 hover:opacity-70" data-id="element-262">
                <Download className="w-4 h-4" data-id="element-263" />
                <span className="hidden md:inline" data-id="element-264">Export</span>
              </button>
              <button onClick={handleNewProject} className="px-6 py-3 bg-white text-black font-bold rounded-full flex items-center gap-2 transition-opacity duration-300 hover:opacity-70" data-id="element-265">
                <Plus className="w-4 h-4" data-id="element-266" />
                Add {activeSection === 'work' ? 'Project' : 'Item'}
              </button>
            </div>
          </div>

          {/* Form rendering - keeping existing form JSX */}
          {isEditing && <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} className="mb-16 border border-white/20 rounded-sm bg-neutral-950 p-8" data-id="element-267">
              <div className="flex items-center justify-between mb-6" data-id="element-268">
                <h2 className="text-2xl font-bold tracking-tight uppercase font-light" style={{
              color: '#999'
            }} data-id="element-269">
                  {activeSection === 'work' ? editingProject ? 'Edit Project' : 'New Project' : editingProjectsItem ? 'Edit Project' : 'New Project'}
                </h2>
                <button onClick={handleCancel} className="transition-opacity duration-300 hover:opacity-70" data-id="element-270">
                  <X className="w-6 h-6" data-id="element-271" />
                </button>
              </div>

              <form onSubmit={handleSubmit} className="space-y-6" data-id="element-272">
                <div data-id="element-273">
                  <label className="block text-sm uppercase tracking-widest font-light mb-2" style={{
                color: '#999'
              }} data-id="element-274">
                    Title *
                  </label>
                  <input type="text" value={activeSection === 'work' ? formData.title : projectsFormData.title} onChange={e => activeSection === 'work' ? setFormData({
                ...formData,
                title: e.target.value
              }) : setProjectsFormData({
                ...projectsFormData,
                title: e.target.value
              })} className="w-full bg-black border border-white/20 rounded-sm px-4 py-3 text-white focus:outline-none focus:border-white/40 transition-colors" required data-id="element-275" />
                </div>

                {activeSection === 'work' && <>
                    <div data-id="element-276">
                      <label className="block text-sm uppercase tracking-widest font-light mb-3" style={{
                  color: '#999'
                }} data-id="element-277">
                        Categories * (Select one or more)
                      </label>
                      <div className="flex flex-wrap gap-3" data-id="element-278">
                        {categories.map(cat => {
                    const isSelected = Array.isArray(formData.category) ? formData.category.includes(cat) : formData.category === cat;
                    return <button key={cat} type="button" onClick={() => handleCategoryToggle(cat)} className={`px-4 py-2 rounded-full border transition-all ${isSelected ? 'bg-white text-black border-white' : 'bg-transparent text-white border-white/20 hover:border-white/40'}`} data-id="element-279">
                              {cat}
                            </button>;
                  })}
                      </div>
                    </div>
                    <div data-id="element-280">
                      <label className="block text-sm uppercase tracking-widest font-light mb-2" style={{
                  color: '#999'
                }} data-id="element-281">
                        Year
                      </label>
                      <input type="text" value={formData.year} onChange={e => setFormData({
                  ...formData,
                  year: e.target.value
                })} className="w-full bg-black border border-white/20 rounded-sm px-4 py-3 text-white focus:outline-none focus:border-white/40 transition-colors" data-id="element-282" />
                    </div>
                    <div data-id="element-283">
                      <label className="block text-sm uppercase tracking-widest font-light mb-2" style={{
                  color: '#999'
                }} data-id="element-284">
                        Description (Optional)
                      </label>
                      <textarea value={formData.description} onChange={e => setFormData({
                  ...formData,
                  description: e.target.value
                })} className="w-full bg-black border border-white/20 rounded-sm px-4 py-3 text-white focus:outline-none focus:border-white/40 transition-colors h-32 resize-none" placeholder="About this piece..." data-id="element-285" />
                      <p className="text-xs mt-2" style={{
                  color: '#666'
                }} data-id="element-286">
                        Optional description about the work
                      </p>
                    </div>
                  </>}

                {activeSection === 'projects' && <>
                    <div data-id="element-287">
                      <label className="block text-sm uppercase tracking-widest font-light mb-2" style={{
                  color: '#999'
                }} data-id="element-288">
                        Description *
                      </label>
                      <textarea value={projectsFormData.description} onChange={e => setProjectsFormData({
                  ...projectsFormData,
                  description: e.target.value
                })} className="w-full bg-black border border-white/20 rounded-sm px-4 py-3 text-white focus:outline-none focus:border-white/40 transition-colors h-32 resize-none" required data-id="element-289" />
                    </div>
                    <div data-id="element-290">
                      <label className="block text-sm uppercase tracking-widest font-light mb-3" style={{
                  color: '#999'
                }} data-id="element-291">
                        Category *
                      </label>
                      <div className="flex flex-wrap gap-3" data-id="element-292">
                        {projectsCategories.map(cat => {
                    const isSelected = projectsFormData.category === cat;
                    return <button key={cat} type="button" onClick={() => setProjectsFormData({
                      ...projectsFormData,
                      category: cat
                    })} className={`px-4 py-2 rounded-full border transition-all ${isSelected ? 'bg-white text-black border-white' : 'bg-transparent text-white border-white/20 hover:border-white/40'}`} data-id="element-293">
                              {cat}
                            </button>;
                  })}
                      </div>
                    </div>
                    <div data-id="element-294">
                      <label className="block text-sm uppercase tracking-widest font-light mb-2" style={{
                  color: '#999'
                }} data-id="element-295">
                        External Link
                      </label>
                      <input type="url" value={projectsFormData.link} onChange={e => setProjectsFormData({
                  ...projectsFormData,
                  link: e.target.value
                })} className="w-full bg-black border border-white/20 rounded-sm px-4 py-3 text-white focus:outline-none focus:border-white/40 transition-colors" placeholder="https://example.com (optional)" data-id="element-296" />
                      <p className="text-xs mt-2" style={{
                  color: '#666'
                }} data-id="element-297">
                        Optional - Leave empty if button should be disabled
                      </p>
                    </div>
                    <div data-id="element-298">
                      <label className="block text-sm uppercase tracking-widest font-light mb-2" style={{
                  color: '#999'
                }} data-id="element-299">
                        Button Text
                      </label>
                      <input type="text" value={projectsFormData.button_text} onChange={e => setProjectsFormData({
                  ...projectsFormData,
                  button_text: e.target.value
                })} className="w-full bg-black border border-white/20 rounded-sm px-4 py-3 text-white focus:outline-none focus:border-white/40 transition-colors" placeholder="View Project" data-id="element-300" />
                      <p className="text-xs mt-2" style={{
                  color: '#666'
                }} data-id="element-301">
                        Default: "View Project"
                      </p>
                    </div>
                    <div data-id="element-302">
                      <label className="flex items-center gap-3 cursor-pointer" data-id="element-303">
                        <input type="checkbox" checked={projectsFormData.button_disabled || false} onChange={e => setProjectsFormData({
                    ...projectsFormData,
                    button_disabled: e.target.checked
                  })} className="w-5 h-5 rounded border-white/20 bg-black checked:bg-white checked:border-white transition-colors cursor-pointer" data-id="element-304" />
                        <div data-id="element-305">
                          <span className="text-sm uppercase tracking-widest font-light" style={{
                      color: '#999'
                    }} data-id="element-306">
                            Disable Button
                          </span>
                          <p className="text-xs mt-1" style={{
                      color: '#666'
                    }} data-id="element-307">
                            Button will show with reduced opacity and no link
                          </p>
                        </div>
                      </label>
                    </div>
                  </>}

                {/* File Upload */}
                <div data-id="element-308">
                  <label className="block text-sm uppercase tracking-widest font-light mb-2" style={{
                color: '#999'
              }} data-id="element-309">
                    {activeSection === 'work' && isVideoCategory() ? 'Video/Media *' : 'Image *'}
                  </label>
                  <div onDrop={handleDrop} onDragOver={e => {
                e.preventDefault();
                setIsDragging(true);
              }} onDragLeave={() => setIsDragging(false)} onClick={() => fileInputRef.current?.click()} className={`relative w-full min-h-[120px] border-2 border-dashed rounded-sm flex flex-col items-center justify-center gap-3 cursor-pointer transition-all ${isDragging ? 'border-white/60 bg-white/5' : 'border-white/20 hover:border-white/40 hover:bg-white/5'}`} data-id="element-310">
                    <Upload className="w-8 h-8 text-white/40" data-id="element-311" />
                    <div className="text-center" data-id="element-312">
                      <p className="text-white/60 text-sm mb-1" data-id="element-313">
                        {activeSection === 'work' && isVideoCategory() ? 'Drag & drop a video or image here' : 'Drag & drop an image here'}
                      </p>
                      <p className="text-white/40 text-xs" data-id="element-314">
                        or click to browse
                      </p>
                    </div>
                    {tempImagePreview && <div className="absolute inset-0 rounded-sm overflow-hidden" data-id="element-315">
                        {formData.media_type === 'video' ? <video src={tempImagePreview} className="w-full h-full object-cover" data-id="element-316" /> : <img src={tempImagePreview} alt="Preview" className="w-full h-full object-cover" data-id="element-317" />}
                      </div>}
                  </div>
                  <input ref={fileInputRef} type="file" accept={activeSection === 'work' && isVideoCategory() ? 'image/*,video/*' : 'image/*'} onChange={e => {
                const file = e.target.files?.[0];
                if (file) handleFileChange(file);
              }} className="hidden" data-id="element-318" />
                </div>

                {activeSection === 'work' && isVideoCategory() && <div data-id="element-319">
                    <label className="block text-sm uppercase tracking-widest font-light mb-2" style={{
                color: '#999'
              }} data-id="element-320">
                      Thumbnail Image * (for Home grid)
                    </label>
                    <div onDrop={handleThumbnailDrop} onDragOver={e => {
                e.preventDefault();
                setIsThumbnailDragging(true);
              }} onDragLeave={() => setIsThumbnailDragging(false)} onClick={() => thumbnailInputRef.current?.click()} className={`relative w-full min-h-[120px] border-2 border-dashed rounded-sm flex flex-col items-center justify-center gap-3 cursor-pointer transition-all ${isThumbnailDragging ? 'border-white/60 bg-white/5' : 'border-white/20 hover:border-white/40 hover:bg-white/5'}`} data-id="element-321">
                      <Upload className="w-8 h-8 text-white/40" data-id="element-322" />
                      <div className="text-center" data-id="element-323">
                        <p className="text-white/60 text-sm mb-1" data-id="element-324">
                          Drag & drop thumbnail image here
                        </p>
                        <p className="text-white/40 text-xs" data-id="element-325">
                          or click to browse
                        </p>
                      </div>
                      {tempThumbnailPreview && <div className="absolute inset-0 rounded-sm overflow-hidden" data-id="element-326">
                          <img src={tempThumbnailPreview} alt="Thumbnail Preview" className="w-full h-full object-cover" data-id="element-327" />
                        </div>}
                    </div>
                    <input ref={thumbnailInputRef} type="file" accept="image/*" onChange={e => {
                const file = e.target.files?.[0];
                if (file) handleThumbnailChange(file);
              }} className="hidden" data-id="element-328" />
                  </div>}

                <div className="flex gap-4" data-id="element-329">
                  <button type="submit" disabled={isUploading} className="px-8 py-4 bg-white text-black font-bold rounded-full flex items-center gap-2 transition-opacity duration-300 hover:opacity-70 disabled:opacity-50" data-id="element-330">
                    <Save className="w-4 h-4" data-id="element-331" />
                    {isUploading ? 'Saving...' : activeSection === 'work' ? editingProject ? 'Update' : 'Create' : editingProjectsItem ? 'Update' : 'Create'}
                  </button>
                  <button type="button" onClick={handleCancel} className="px-8 py-4 border border-white/20 text-white font-bold rounded-full transition-opacity duration-300 hover:opacity-70" data-id="element-332">
                    Cancel
                  </button>
                </div>
              </form>
            </motion.div>}

          {/* List */}
          <div className="space-y-4" data-id="element-333">
            {isLoadingData ? <div className="text-center py-16" data-id="element-334">
                <p className="text-xl" style={{
              color: '#999'
            }} data-id="element-335">
                  Loading...
                </p>
              </div> : currentData.length === 0 ? <div className="text-center py-16" data-id="element-336">
                <p className="text-xl" style={{
              color: '#999'
            }} data-id="element-337">
                  No {activeSection === 'work' ? 'work' : 'projects'} yet. Add
                  some above.
                </p>
              </div> : <Reorder.Group axis="y" values={currentData} onReorder={handleReorder} className="space-y-4" data-id="element-338">
                {currentData.map((item: any) => <Reorder.Item key={item.id} value={item} className="p-6 border border-white/20 rounded-sm bg-neutral-950 flex items-center gap-6 hover:border-white/40 transition-colors cursor-grab active:cursor-grabbing" whileDrag={{
              scale: 1.02,
              boxShadow: '0 10px 30px rgba(0,0,0,0.3)'
            }} data-id="element-339">
                    <div className="flex-shrink-0 cursor-grab active:cursor-grabbing" data-id="element-340">
                      <GripVertical className="w-5 h-5" style={{
                  color: '#666'
                }} data-id="element-341" />
                    </div>
                    <div className="w-32 h-20 bg-neutral-900 rounded-sm overflow-hidden flex-shrink-0" data-id="element-342">
                      <img src={activeSection === 'work' ? getDisplayImage(item) : getImageUrl(item.image_path)} alt={item.title} className="w-full h-full object-cover" data-id="element-343" />
                    </div>
                    <div className="flex-1" data-id="element-344">
                      <h3 className="text-xl font-bold mb-1" data-id="element-345">{item.title}</h3>
                      <p className="text-sm" style={{
                  color: '#999'
                }} data-id="element-346">
                        {activeSection === 'work' ? `${getCategoryDisplay(item.category)} • ${item.year}` : `${item.category || 'Uncategorized'} • ${item.description.substring(0, 60)}...`}
                      </p>
                    </div>
                    <div className="flex gap-2" data-id="element-347">
                      <button onClick={() => activeSection === 'work' ? handleEdit(item) : handleEditProjectsItem(item)} className="p-3 border border-white/20 rounded-full transition-opacity duration-300 hover:opacity-70" data-id="element-348">
                        <Edit2 className="w-4 h-4" data-id="element-349" />
                      </button>
                      <button onClick={() => activeSection === 'work' ? handleDelete(item.id) : handleDeleteProjectsItem(item.id)} className="p-3 border border-white/20 rounded-full transition-opacity duration-300 hover:opacity-70" data-id="element-350">
                        <Trash2 className="w-4 h-4" data-id="element-351" />
                      </button>
                    </div>
                  </Reorder.Item>)}
              </Reorder.Group>}
          </div>
        </div>
      </main>
    </div>;
}